import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, UserPlus, Shield, ShieldCheck, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('moderator');
  const [creating, setCreating] = useState(false);
  
  // Delete confirmation
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    // Check if user is admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }
    
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
        navigate('/');
      } else {
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()) {
      return;
    }
    
    try {
      setCreating(true);
      await axios.post(`${API}/admin/users`, {
        name: newUserName.trim(),
        email: newUserEmail.trim(),
        password: newUserPassword,
        role: newUserRole
      }, {
        headers: getAuthHeaders()
      });
      
      setShowCreateDialog(false);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('moderator');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handlePromote = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/promote`, null, {
        headers: getAuthHeaders()
      });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to promote user');
    }
  };

  const handleDemote = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/demote`, null, {
        headers: getAuthHeaders()
      });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to demote user');
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;
    
    try {
      await axios.delete(`${API}/admin/users/${deleteUserId}`, {
        headers: getAuthHeaders()
      });
      setShowDeleteDialog(false);
      setDeleteUserId(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const confirmDelete = (userId) => {
    setDeleteUserId(userId);
    setShowDeleteDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-white flex items-center gap-2">
                <ShieldCheck className="w-8 h-8 text-blue-400" />
                Admin Panel
              </h1>
              <p className="text-slate-400 font-body">Manage users and permissions</p>
            </div>
          </div>
          
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Users List */}
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader>
            <CardTitle className="text-white font-heading">
              Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-[#0F172A] rounded-lg"
                  data-testid={`user-row-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.role === 'admin' ? 'bg-blue-600' : 'bg-slate-600'
                    }`}>
                      {user.role === 'admin' ? (
                        <ShieldCheck className="w-5 h-5 text-white" />
                      ) : (
                        <Shield className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{user.name}</p>
                      <p className="text-slate-400 text-sm">{user.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      user.role === 'admin' 
                        ? 'bg-blue-900/50 text-blue-300 border border-blue-700' 
                        : 'bg-slate-700 text-slate-300 border border-slate-600'
                    }`}>
                      {user.role.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {user.id !== currentUser?.id && (
                      <>
                        {user.role === 'moderator' ? (
                          <Button
                            onClick={() => handlePromote(user.id)}
                            variant="outline"
                            size="sm"
                            className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                            data-testid={`promote-${user.id}`}
                          >
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Promote
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleDemote(user.id)}
                            variant="outline"
                            size="sm"
                            className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                            data-testid={`demote-${user.id}`}
                          >
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Demote
                          </Button>
                        )}
                        <Button
                          onClick={() => confirmDelete(user.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-600 text-red-400 hover:bg-red-900/30"
                          data-testid={`delete-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {user.id === currentUser?.id && (
                      <span className="text-slate-500 text-sm italic">You</span>
                    )}
                  </div>
                </div>
              ))}
              
              {users.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  No users found
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role Legend */}
        <div className="mt-6 p-4 bg-[#1E293B] rounded-lg border border-slate-700">
          <h3 className="text-white font-semibold mb-3">Role Permissions</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="flex items-center gap-2 text-blue-400 font-semibold mb-1">
                <ShieldCheck className="w-4 h-4" />
                Admin
              </div>
              <ul className="text-slate-400 list-disc list-inside">
                <li>Create and delete games</li>
                <li>Control all game features</li>
                <li>Manage users and roles</li>
                <li>Full system access</li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-300 font-semibold mb-1">
                <Shield className="w-4 h-4" />
                Moderator
              </div>
              <ul className="text-slate-400 list-disc list-inside">
                <li>Control existing games</li>
                <li>Update scores, cards, timer</li>
                <li>Access all overlays</li>
                <li>Cannot create/delete games</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-400" />
              Create New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-slate-300 text-sm font-body block mb-2">Name *</label>
              <Input
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                placeholder="Enter name..."
                className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <label className="text-slate-300 text-sm font-body block mb-2">Email *</label>
              <Input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter email..."
                className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <label className="text-slate-300 text-sm font-body block mb-2">Password *</label>
              <Input
                type="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="Enter password..."
                className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <label className="text-slate-300 text-sm font-body block mb-2">Role</label>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-slate-600">
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => setShowCreateDialog(false)}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={creating || !newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {creating ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-slate-300">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowDeleteDialog(false)}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
